import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, CheckCircle2 } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const emptyEmp = {
  fld_Employeecode: "",
  fld_Employee: "",
  fld_lname: "",
  fld_fathername: "",
  fld_deptid: "",
  fld_desigid: "",
  fld_gender: "",
  fld_bloodgroup: "",
  fld_dateofjoining: "",
  fld_typeid: "",

  fld_bankname: "",
  fld_accountno: "",
  fld_IFSCCode: "",

  fld_tempaddress: "",
  fld_permaddress: "",

  fld_countryid: "",
  fld_stateid: "",
  fld_districtid: "",
  fld_cityid: "",

  fld_phone: "",
  fld_mobile: "",
  fld_emailid: "",

  fld_maritalstatus: "",
  fld_dateofbirth: "",
  fld_PANno: "",
  fld_Aadharcardnumber: "",
  fld_voterid: "",
  fld_drivinglicnum: "",
  fld_drivinglicexpdate: "",
  fld_pfuanno: "",

  fld_preexpyrs: "",
  fld_preexpmonths: "",

  fld_grosssalary: "",
  fld_SpecialHRA: "",
  fld_conveyanceallowance: "",
  fld_pfno: "",
  fld_pf: "",
  fld_tds: "",
  fld_ESIno: "",
  fld_esi: "",
  fld_gradeid: "",
  fld_branchid: "",
  fld_salarybasis: "",
  fld_schemeid: "",
  fld_leaveOB: "",
  fld_leavepermonth: "",
  fld_dailybata: "",
  fld_paymode: "",

  fld_ssnno: "",
  fld_shiftname: "",
  fld_claallowance: "",
  fld_leaveclosingbalance: "",
  fld_leaveeligible: "",
  fld_incometax: "",
  fld_availleave: "",
  fld_bonusbata: "",
  fld_details: "",
};

// ✅ Reusable Field Components (MUST be above EmployeeManagementForm component)
// Fixes: input focus lost / only one character typed issue
const fieldHeight = "h-[52px]"; // same as selects

export const SelectField = ({
  label,
  required,
  value,
  onChange,
  options,
  placeholder = "Select",
  inputBase,
  labelBase,
}) => (
  <div>
    <div className={labelBase}>
      {label} {required ? <span className="text-red-500">*</span> : null}
    </div>
    <select
      className={`${inputBase} ${fieldHeight}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {(options || []).map((o) => (
        <option key={String(o.value)} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

export const TextField = ({
  label,
  required,
  value,
  onChange,
  placeholder = "",
  inputBase,
  labelBase,
  type = "text",
}) => (
  <div>
    <div className={labelBase}>
      {label} {required ? <span className="text-red-500">*</span> : null}
    </div>
    <input
      type={type}
      className={`${inputBase} ${fieldHeight}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

export const DateField = ({
  label,
  required,
  value,
  onChange,
  inputBase,
  labelBase,
}) => (
  <div>
    <div className={labelBase}>
      {label} {required ? <span className="text-red-500">*</span> : null}
    </div>
    <input
      type="date"
      className={`${inputBase} ${fieldHeight}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export const RadioField = ({
  label,
  required,
  value,
  onChange,
  options,
  isDark,
  labelBase,
}) => (
  <div>
    <div className={labelBase}>
      {label} {required ? <span className="text-red-500">*</span> : null}
    </div>

    <div
      className={`flex items-center gap-6 mt-2 px-4 py-3 rounded-2xl border ${
        isDark ? "border-white/10 bg-gray-800/30" : "border-gray-200 bg-white"
      }`}
    >
      {(options || []).map((o) => (
        <label
          key={String(o.value)}
          className="inline-flex items-center gap-2 cursor-pointer"
        >
          <input
            type="radio"
            name="gender" // ✅ keep fixed for Sex (only used in Basic tab)
            value={o.value}
            checked={(value || "") === o.value}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className={isDark ? "text-gray-200" : "text-gray-800"}>
            {o.label}
          </span>
        </label>
      ))}
    </div>
  </div>
);

const EmployeeManagementForm = () => {
  const { employeeId } = useParams(); // undefined for /new
  const isNew = !employeeId || employeeId === "new";
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);

  const finYearId = Number(localStorage.getItem("finYearId") || 0);
  const userId = Number(localStorage.getItem("userId") || 1);

  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("basic");
  const TAB_KEY = "empMgmt_activeTab";

  const setTabSafe = (t) => {
    setTab(t);
    sessionStorage.setItem(TAB_KEY, t);
  };

  const screenBadge = useMemo(() => "INFRA SCREEN : EMPLOYEE MANAGEMENT", []);

  // masters
  const [masters, setMasters] = useState({
    branches: [],
    departments: [],
    designations: [],
    grades: [],
    empTypes: [],
    schemes: [],
    countries: [],
    states: [],
    districts: [],
    cities: [],
  });

  // data from API (master + queue)
  const [server, setServer] = useState({
    employeeMaster: null,
    employeeApprovalQueue: null,
    latestRemark: null,
    qualifications: [],
  });

  // editable model
  const [emp, setEmp] = useState({ ...emptyEmp });
  const [remarkTitle, setRemarkTitle] = useState("");
  const [remarkDetails, setRemarkDetails] = useState("");

  // qualification UI
  const [qualifications, setQualifications] = useState([]);
  const [qualName, setQualName] = useState("");
  const [qualYear, setQualYear] = useState("");
  const [qualSelected, setQualSelected] = useState([]);

  // photo
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  // ✅ Draft-New mode: stays "Save" even after auto-create for photo/qualification
  const DRAFT_FLAG_KEY = "empMgmt_isDraftNew";
  const DRAFT_ID_KEY = "empMgmt_draftEmployeeId";

  const [draftNew, setDraftNew] = useState(() => {
    // when route is /new => draftNew must be true
    if (isNew) return true;

    const flag = sessionStorage.getItem(DRAFT_FLAG_KEY) === "1";
    const did = sessionStorage.getItem(DRAFT_ID_KEY);
    return flag && did === String(employeeId);
  });

  const ensureFinYear = () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 bg-emerald-600 hover:bg-emerald-700`;

  const backBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const setField = (k, v) => setEmp((p) => ({ ...p, [k]: v }));

  const getBasicMissing = () => {
    const miss = [];
    if (!emp.fld_deptid) miss.push("Department");
    if (!emp.fld_desigid) miss.push("Designation");
    if (!emp.fld_Employee?.toString().trim()) miss.push("Employee Name");
    if (!emp.fld_Employeecode?.toString().trim()) miss.push("Employee Code");
    if (!emp.fld_gender) miss.push("Sex");
    if (!emp.fld_dateofjoining) miss.push("Date of Joining");
    if (!emp.fld_typeid) miss.push("Employee Type");
    return miss;
  };

  const isBasicValid = () => getBasicMissing().length === 0;

  const TabBtn = ({ id, label }) => (
    <button
      onClick={() => {
        if (id === "basic") return setTabSafe("basic");

        if (!isBasicValid()) {
          const miss = getBasicMissing();
          toast.warn(`Fill mandatory fields in Basic: ${miss.join(", ")}`);
          setTabSafe("basic");
          return;
        }

        setTabSafe(id);
      }}
      className={`px-4 py-2 rounded-2xl text-sm font-extrabold border transition ${
        tab === id
          ? isDark
            ? "bg-white/10 border-white/10"
            : "bg-gray-100 border-gray-200"
          : isDark
            ? "border-white/10 hover:bg-white/5"
            : "border-gray-200 hover:bg-gray-50"
      }`}
      type="button"
    >
      {label}
    </button>
  );

  useEffect(() => {
    const saved = sessionStorage.getItem(TAB_KEY);
    if (saved) setTab(saved);
  }, [employeeId]);

  const saveAndGetIdIfNew = async () => {
    if (!isNew) return Number(employeeId);

    if (!ensureFinYear()) return 0;
    if (!validateBeforeSave()) return 0;

    const res = await axios.post(`${BASE_URL}/api/employee-management`, {
      finYearId,
      userId,
      branchId: emp.fld_branchid || "",
      employee: emp,
      remarkTitle,
      remarkDetails,
    });

    if (!res?.data?.status) {
      toast.error(res?.data?.message || "Save failed");
      return 0;
    }

    const newId = Number(res.data.employeeId || 0);
    if (!newId) {
      toast.error("Save succeeded but employeeId missing");
      return 0;
    }

    // ✅ mark this id as "draft new" so UI stays in Save-mode
    sessionStorage.setItem(DRAFT_FLAG_KEY, "1");
    sessionStorage.setItem(DRAFT_ID_KEY, String(newId));
    setDraftNew(true);

    // move from /new -> /{id}
    sessionStorage.setItem(TAB_KEY, tab);
    navigate(`/PayRoll/EmployeeManagement/${newId}`);
    return newId;
  };

  const loadMasters = async () => {
    if (!ensureFinYear()) return;
    const res = await axios.get(`${BASE_URL}/api/employee-management/masters`, {
      params: { finYearId },
    });
    if (res?.data?.status) setMasters(res.data.data);
    else toast.error(res?.data?.message || "Failed to load masters");
  };

  const loadDetails = async () => {
    if (!ensureFinYear()) return;
    if (isNew) {
      setServer({
        employeeMaster: null,
        employeeApprovalQueue: null,
        latestRemark: null,
        qualifications: [],
      });
      setEmp({ ...emptyEmp });
      setQualifications([]);
      setRemarkTitle("");
      setRemarkDetails("");
      return;
    }

    const res = await axios.get(
      `${BASE_URL}/api/employee-management/${employeeId}/details`,
      {
        params: { finYearId },
      },
    );

    if (res?.data?.status) {
      const data = res.data.data || {};
      setServer(data);

      const base = data.employeeApprovalQueue || data.employeeMaster || {};
      setEmp((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.keys(emptyEmp).map((k) => [k, base[k] ?? ""]),
        ),
      }));

      setQualifications(data.qualifications || []);
      setRemarkTitle(data.latestRemark?.fld_title || "");
      setRemarkDetails(data.latestRemark?.fld_details || "");
    } else {
      toast.error(res?.data?.message || "Failed to load details");
    }
  };

  const loadQualifications = async () => {
    if (isNew) return;
    const res = await axios.get(
      `${BASE_URL}/api/employee-management/${employeeId}/qualifications`,
      {
        params: { finYearId },
      },
    );
    if (res?.data?.status) setQualifications(res.data.data || []);
  };

  useEffect(() => {
    loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    if (isNew) {
      setDraftNew(true);
      sessionStorage.setItem(DRAFT_FLAG_KEY, "1");
      sessionStorage.removeItem(DRAFT_ID_KEY);
      return;
    }

    const flag = sessionStorage.getItem(DRAFT_FLAG_KEY) === "1";
    const did = sessionStorage.getItem(DRAFT_ID_KEY);

    // keep draftNew ONLY for the same newly-created employee id
    setDraftNew(flag && did === String(employeeId));
  }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateBeforeSave = () => {
    if (!emp.fld_accountno?.toString().trim()) {
      toast.warn("Pls enter acc Number (check)");
      return false;
    }

    if (!emp.fld_Employee?.toString().trim()) {
      toast.warn("Employee Name required");
      return false;
    }

    if (!emp.fld_Employeecode?.toString().trim()) {
      toast.warn("Employee Code required");
      return false;
    }

    if (!emp.fld_maritalstatus?.toString().trim()) {
      toast.warn("Marital Status required");
      return false;
    }

    if (!emp.fld_grosssalary?.toString().trim()) {
      toast.warn("Gross Salary required");
      return false;
    }

    if (!emp.fld_SpecialHRA?.toString().trim()) {
      toast.warn("Special HRA required");
      return false;
    }

    if (!emp.fld_conveyanceallowance?.toString().trim()) {
      toast.warn("Conveyance Allowance required");
      return false;
    }

    if (!emp.fld_pfno?.toString().trim()) {
      toast.warn("PF No required");
      return false;
    }

    if (!emp.fld_pf?.toString().trim()) {
      toast.warn("PF required");
      return false;
    }

    if (!emp.fld_tds?.toString().trim()) {
      toast.warn("TDS required");
      return false;
    }

    if (!emp.fld_pfuanno?.toString().trim()) {
      toast.warn("PF UAN No required");
      return false;
    }

    if (!emp.fld_gradeid?.toString().trim()) {
      toast.warn("Grade required");
      return false;
    }

    if (!emp.fld_branchid?.toString().trim()) {
      toast.warn("Branch required");
      return false;
    }

    if (!emp.fld_esi?.toString().trim()) {
      toast.warn("E.S.I. required");
      return false;
    }

    if (!emp.fld_ESIno?.toString().trim()) {
      toast.warn("ESI Number required");
      return false;
    }

    if (!emp.fld_shiftname?.toString().trim()) {
      toast.warn("Shift required");
      return false;
    }

    if (!emp.fld_salarybasis?.toString().trim()) {
      toast.warn("Salary Basis required");
      return false;
    }

    if (!emp.fld_leaveOB?.toString().trim()) {
      toast.warn("Leave Opening Balance required");
      return false;
    }

    if (!emp.fld_leavepermonth?.toString().trim()) {
      toast.warn("Leave Per Month required");
      return false;
    }

    if (!emp.fld_schemeid?.toString().trim()) {
      toast.warn("Salary Scheme required");
      return false;
    }

    if (!emp.fld_paymode?.toString().trim()) {
      toast.warn("Payment Mode required");
      return false;
    }

    return true;
  };

  // ✅ IMPORTANT: Build backend-aligned payload (adds the missing DB columns your API expects)
  const buildEmployeePayload = () => {
    const salaryBasisRaw = (emp.fld_salarybasis || "")
      .toString()
      .trim()
      .toLowerCase();
    const salarybasisid =
      salaryBasisRaw === "daily"
        ? 1
        : salaryBasisRaw === "monthly"
          ? 2
          : emp.fld_salarybasisid || "";

    const payload = {
      ...emp,

      // mirrors / adds DB/API fields
      fld_salarybasisid: salarybasisid,

      // your UI uses camel-ish names; DB uses exact column names:
      fld_CLAallowance: emp.fld_claallowance ?? emp.fld_CLAallowance ?? "",
      fld_bonbata:
        emp.fld_bonusbata === "Yes" ||
        emp.fld_bonusbata === 1 ||
        emp.fld_bonusbata === true
          ? 1
          : 0,

      // SSN column in DB is fld_SSNno (your UI uses fld_ssnno)
      fld_SSNno: emp.fld_ssnno ?? emp.fld_SSNno ?? "",

      // ensure these exist (your backend uses these)
      fld_shiftname: emp.fld_shiftname ?? "",
      fld_incometax: emp.fld_incometax ?? "",
      fld_preexpyrs: emp.fld_preexpyrs ?? "",
      fld_preexpmonths: emp.fld_preexpmonths ?? "",

      // AvailLeave is BIT in DB; your backend converts "Yes"/"No"
      fld_availleave: emp.fld_availleave ?? "",
    };

    return payload;
  };

  const isDirectSaveMode = isNew || draftNew;

  const save = async () => {
    if (!ensureFinYear()) return;
    if (!validateBeforeSave()) return;

    try {
      setLoading(true);

      const employeePayload = buildEmployeePayload();

      if (isDirectSaveMode) {
        // ✅ Direct Save mode (master)
        // If still /new => POST create (works)
        // If already has id (draftNew=true) => DO NOT call PUT queue by mistake
        if (isNew) {
          const res = await axios.post(`${BASE_URL}/api/employee-management`, {
            finYearId,
            userId,
            branchId: employeePayload.fld_branchid || "",
            employee: employeePayload,
            remarkTitle,
            remarkDetails,
          });

          if (res?.data?.status) {
            toast.success(res.data.message || "Saved");
            const newId = res.data.employeeId;

            // keep draft mode ON for this new id
            sessionStorage.setItem(DRAFT_FLAG_KEY, "1");
            sessionStorage.setItem(DRAFT_ID_KEY, String(newId));
            setDraftNew(true);

            navigate(`/PayRoll/EmployeeManagement/${newId}`);
          } else toast.error(res?.data?.message || "Save failed");
        } else {
          // ✅ IMPORTANT:
          // You're on /{id} but still in draftNew flow.
          // DO NOT submit to approval queue using PUT.
          toast.warn(
            "This is a newly created employee draft. Please add photo/qualification, then use Master-Update API to save edits (not Approval PUT).",
          );
          // (Once you create a backend master-update endpoint, call it here.)
        }
      } else {
        // ✅ Approval flow (existing record edit)
        const res = await axios.put(
          `${BASE_URL}/api/employee-management/${employeeId}`,
          {
            finYearId,
            userId,
            branchId: employeePayload.fld_branchid || "",
            employee: employeePayload,
            remarkTitle,
            remarkDetails,
          },
        );

        if (res?.data?.status) {
          toast.success(res.data.message || "Submitted for Approval");
          navigate("/payroll/employee-management");
        } else toast.error(res?.data?.message || "Submit failed");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Save failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const addQualification = async () => {
    try {
      if (!qualName.trim() || !qualYear)
        return toast.warn("Enter Qualification + Year of Completion");

      const id = await saveAndGetIdIfNew();
      if (!id) return;

      const res = await axios.post(
        `${BASE_URL}/api/employee-management/${id}/qualifications`,
        {
          finYearId,
          userId,
          qualificationName: qualName.trim(),
          completionYear: Number(qualYear),
        },
      );

      if (res?.data?.status) {
        toast.success("Added");
        setQualName("");
        setQualYear("");
        setQualSelected([]);
        await loadQualifications();
      } else toast.error(res?.data?.message || "Failed");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed");
    }
  };

  const removeQualifications = async () => {
    if (isNew) return;
    if (qualSelected.length === 0)
      return toast.warn("Select qualification(s) to remove");
    const res = await axios.delete(
      `${BASE_URL}/api/employee-management/${employeeId}/qualifications`,
      {
        data: { finYearId, qualificationIds: qualSelected },
      },
    );

    if (res?.data?.status) {
      toast.success("Removed");
      setQualSelected([]);
      await loadQualifications();
    } else toast.error(res?.data?.message || "Failed");
  };

  const uploadPhoto = async () => {
    try {
      if (!photoFile) return toast.warn("Choose photo first");

      const id = await saveAndGetIdIfNew();
      if (!id) return;

      setPhotoUploading(true);
      const fd = new FormData();
      fd.append("photo", photoFile);
      fd.append("finYearId", String(finYearId));
      fd.append("userId", String(userId));

      const res = await axios.post(
        `${BASE_URL}/api/employee-management/${id}/photo`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res?.data?.status) {
        toast.success("Photo uploaded");
        setPhotoFile(null);
        await loadDetails();
      } else toast.error(res?.data?.message || "Upload failed");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Upload failed",
      );
    } finally {
      setPhotoUploading(false);
    }
  };

  const filename = (
    server?.employeeApprovalQueue?.fld_photo ||
    server?.employeeMaster?.fld_photo ||
    ""
  )
    .toString()
    .trim();

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2600}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              {isNew ? "ADD EMPLOYEE" : "EDIT EMPLOYEE"}
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
          onClick={() => navigate(-1)}
          type="button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div
          className={`mx-auto max-w-6xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div>
              <div className="text-2xl font-extrabold">
                {isNew ? "New Employee" : `Employee ID: ${employeeId}`}
              </div>
              <div className="text-sm opacity-70">
                Edit = Submit to Approval Queue (ASP flow) • Add = Direct Save
              </div>
            </div>

            <div className="flex flex-wrap gap-3 justify-end">
              <button
                className={`${primaryBtn} ${!isBasicValid() ? "opacity-60 cursor-not-allowed" : ""}`}
                onClick={save}
                disabled={loading || !isBasicValid()}
                type="button"
                title={
                  !isBasicValid() ? `Fill: ${getBasicMissing().join(", ")}` : ""
                }
              >
                <CheckCircle2 className="w-4 h-4" />
                {isDirectSaveMode ? "Save" : "Submit for Approval"}
              </button>
            </div>
          </div>

          <div
            className={`px-4 py-2 rounded-2xl text-sm font-bold inline-block ${
              isDark
                ? "bg-blue-900/40 text-blue-100"
                : "bg-blue-100 text-blue-800"
            }`}
          >
            {screenBadge}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <TabBtn id="basic" label="BASIC DETAILS" />
            <TabBtn id="contact" label="CONTACT INFORMATION" />
            <TabBtn id="personal" label="PERSONAL DETAILS" />
            <TabBtn id="status" label="STATUS" />
          </div>

          <div className={`${cardBase} p-6 mt-4`}>
            {loading ? (
              <div className="py-10 text-center opacity-70">Loading...</div>
            ) : (
              <>
                {tab === "basic" && (
                  <>
                    <div className="text-xl font-extrabold mb-4">
                      Basic Details
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <SelectField
                        label="Department"
                        required
                        value={emp.fld_deptid}
                        onChange={(v) => setField("fld_deptid", v)}
                        options={(masters.departments || []).map((d) => ({
                          value: d.deptId,
                          label: d.deptName,
                        }))}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <SelectField
                        label="Designation"
                        required
                        value={emp.fld_desigid}
                        onChange={(v) => setField("fld_desigid", v)}
                        options={(masters.designations || []).map((d) => ({
                          value: d.desigId,
                          label: d.desigName,
                        }))}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <TextField
                        label="Employee Code"
                        required
                        value={emp.fld_Employeecode}
                        onChange={(v) => setField("fld_Employeecode", v)}
                        placeholder="Employee Code"
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <TextField
                        label="Employee Name"
                        required
                        value={emp.fld_Employee}
                        onChange={(v) => setField("fld_Employee", v)}
                        placeholder="Employee Name"
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <TextField
                        label="Last Name"
                        value={emp.fld_lname}
                        onChange={(v) => setField("fld_lname", v)}
                        placeholder="Last Name"
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <TextField
                        label="Father Name"
                        value={emp.fld_fathername}
                        onChange={(v) => setField("fld_fathername", v)}
                        placeholder="Father Name"
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <RadioField
                        label="Sex"
                        required
                        value={emp.fld_gender}
                        onChange={(v) => setField("fld_gender", v)}
                        options={[
                          { value: "Male", label: "Male" },
                          { value: "Female", label: "Female" },
                        ]}
                        isDark={isDark}
                        labelBase={labelBase}
                      />

                      <SelectField
                        label="Blood Group"
                        value={emp.fld_bloodgroup}
                        onChange={(v) => setField("fld_bloodgroup", v)}
                        options={[
                          { value: "O+", label: "O+" },
                          { value: "O-", label: "O-" },
                          { value: "A+", label: "A+" },
                          { value: "A-", label: "A-" },
                          { value: "B+", label: "B+" },
                          { value: "B-", label: "B-" },
                          { value: "AB+", label: "AB+" },
                          { value: "AB-", label: "AB-" },
                        ]}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <DateField
                        label="Date of Joining"
                        required
                        value={emp.fld_dateofjoining}
                        onChange={(v) => setField("fld_dateofjoining", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <SelectField
                        label="Employee Type"
                        required
                        value={emp.fld_typeid}
                        onChange={(v) => setField("fld_typeid", v)}
                        options={(masters.empTypes || []).map((t) => ({
                          value: t.typeId,
                          label: t.typeName,
                        }))}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <TextField
                        label="Bank Name"
                        value={emp.fld_bankname}
                        onChange={(v) => setField("fld_bankname", v)}
                        placeholder="Bank Name"
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <TextField
                        label="A/C Number"
                        required
                        value={emp.fld_accountno}
                        onChange={(v) => setField("fld_accountno", v)}
                        placeholder="A/C Number"
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />
                    </div>
                  </>
                )}

                {tab === "contact" && (
                  <>
                    <div className="text-xl font-extrabold mb-4">
                      Contact Information
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className={labelBase}>Temporary Address</div>
                        <input
                          className={inputBase}
                          value={emp.fld_tempaddress}
                          onChange={(e) =>
                            setField("fld_tempaddress", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <div className={labelBase}>Permanent Address</div>
                        <input
                          className={inputBase}
                          value={emp.fld_permaddress}
                          onChange={(e) =>
                            setField("fld_permaddress", e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <div className={labelBase}>Country</div>
                        <select
                          className={inputBase}
                          value={emp.fld_countryid}
                          onChange={(e) =>
                            setField("fld_countryid", e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          {masters.countries.map((c) => (
                            <option key={c.countryId} value={c.countryId}>
                              {c.countryName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className={labelBase}>State</div>
                        <select
                          className={inputBase}
                          value={emp.fld_stateid}
                          onChange={(e) =>
                            setField("fld_stateid", e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          {masters.states.map((s) => (
                            <option key={s.stateId} value={s.stateId}>
                              {s.stateName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className={labelBase}>District</div>
                        <select
                          className={inputBase}
                          value={emp.fld_districtid}
                          onChange={(e) =>
                            setField("fld_districtid", e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          {masters.districts.map((d) => (
                            <option key={d.districtId} value={d.districtId}>
                              {d.districtName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className={labelBase}>City</div>
                        <select
                          className={inputBase}
                          value={emp.fld_cityid}
                          onChange={(e) =>
                            setField("fld_cityid", e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          {masters.cities.map((c) => (
                            <option key={c.cityId} value={c.cityId}>
                              {c.cityName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className={labelBase}>Phone</div>
                        <input
                          className={inputBase}
                          value={emp.fld_phone}
                          onChange={(e) =>
                            setField("fld_phone", e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <div className={labelBase}>Mobile</div>
                        <input
                          className={inputBase}
                          value={emp.fld_mobile}
                          onChange={(e) =>
                            setField("fld_mobile", e.target.value)
                          }
                        />
                      </div>

                      <div>
                        <div className={labelBase}>Email</div>
                        <input
                          className={inputBase}
                          value={emp.fld_emailid}
                          onChange={(e) =>
                            setField("fld_emailid", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </>
                )}

                {tab === "personal" && (
                  <>
                    <div className="text-xl font-extrabold mb-4">
                      Personal Details
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="p-4 rounded-2xl border border-gray-200 dark:border-white/10">
                        <div className="font-extrabold mb-2">Photo</div>

                        {!filename ? (
                          <div className="text-sm opacity-70">
                            No photo uploaded.
                          </div>
                        ) : (
                          <img
                            alt="Employee"
                            src={`${BASE_URL}/employee_photo/${filename}`}
                            className="w-40 h-40 object-cover rounded-2xl border mt-2"
                          />
                        )}

                        <div className="mt-3 flex flex-col gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                              setPhotoFile(e.target.files?.[0] || null)
                            }
                          />
                          <button
                            type="button"
                            onClick={uploadPhoto}
                            disabled={photoUploading}
                            className="px-5 py-2 rounded-2xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-60"
                          >
                            {photoUploading ? "Uploading..." : "Upload Photo"}
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <SelectField
                            label="Marital Status"
                            required
                            value={emp.fld_maritalstatus}
                            onChange={(v) => setField("fld_maritalstatus", v)}
                            options={[
                              { value: "Single", label: "Single" },
                              { value: "Married", label: "Married" },
                            ]}
                            inputBase={inputBase}
                            labelBase={labelBase}
                          />
                        </div>
                        <div>
                          <div className={labelBase}>Date of Birth</div>
                          <input
                            type="date"
                            className={inputBase}
                            value={emp.fld_dateofbirth}
                            onChange={(e) =>
                              setField("fld_dateofbirth", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <div className={labelBase}>PAN No</div>
                          <input
                            className={inputBase}
                            value={emp.fld_PANno}
                            onChange={(e) =>
                              setField("fld_PANno", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <div className={labelBase}>Aadhar No</div>
                          <input
                            className={inputBase}
                            value={emp.fld_Aadharcardnumber}
                            onChange={(e) =>
                              setField("fld_Aadharcardnumber", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <div className={labelBase}>Voter ID</div>
                          <input
                            className={inputBase}
                            value={emp.fld_voterid}
                            onChange={(e) =>
                              setField("fld_voterid", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <div className={labelBase}>Driving Lic No</div>
                          <input
                            className={inputBase}
                            value={emp.fld_drivinglicnum}
                            onChange={(e) =>
                              setField("fld_drivinglicnum", e.target.value)
                            }
                          />
                        </div>
                        <div className="md:col-span-2">
                          <div className={labelBase}>Driving Lic Exp Date</div>
                          <input
                            type="date"
                            className={inputBase}
                            value={emp.fld_drivinglicexpdate}
                            onChange={(e) =>
                              setField("fld_drivinglicexpdate", e.target.value)
                            }
                          />
                        </div>
                        <div className="md:col-span-2">
                          <div className={labelBase}>PF UAN No</div>
                          <input
                            className={inputBase}
                            value={emp.fld_pfuanno}
                            onChange={(e) =>
                              setField("fld_pfuanno", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Qualifications */}
                    <div className="p-5 rounded-3xl border border-gray-200 dark:border-white/10">
                      <div className="text-lg font-extrabold mb-3">
                        Qualification
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                        <div>
                          <div className={labelBase}>Qualification</div>
                          <input
                            className={inputBase}
                            value={qualName}
                            onChange={(e) => setQualName(e.target.value)}
                          />
                        </div>
                        <div>
                          <div className={labelBase}>Year Of Completion</div>
                          <input
                            className={inputBase}
                            value={qualYear}
                            onChange={(e) => setQualYear(e.target.value)}
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={addQualification}
                            className="px-5 py-2 rounded-2xl bg-emerald-600 text-white font-bold hover:bg-emerald-700"
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={removeQualifications}
                            className="px-5 py-2 rounded-2xl bg-red-600 text-white font-bold hover:bg-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-blue-700 text-white">
                            <tr>
                              <th className="px-3 py-2 text-center">S.No</th>
                              <th className="px-3 py-2 text-center">
                                Qualification
                              </th>
                              <th className="px-3 py-2 text-center">
                                Year Of Completion
                              </th>
                              <th className="px-3 py-2 text-center">Delete</th>
                            </tr>
                          </thead>
                          <tbody>
                            {qualifications.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-3 py-6 text-center opacity-70"
                                >
                                  No qualifications
                                </td>
                              </tr>
                            ) : (
                              qualifications.map((q, idx) => (
                                <tr
                                  key={q.qualificationId}
                                  className="border-b"
                                >
                                  <td className="px-3 py-2 text-center">
                                    {idx + 1}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {q.qualificationName}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {q.completionYear}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <input
                                      type="checkbox"
                                      checked={qualSelected.includes(
                                        q.qualificationId,
                                      )}
                                      onChange={() =>
                                        setQualSelected((prev) =>
                                          prev.includes(q.qualificationId)
                                            ? prev.filter(
                                                (x) => x !== q.qualificationId,
                                              )
                                            : [...prev, q.qualificationId],
                                        )
                                      }
                                    />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* PRE EXPERIENCE (ASP) */}
                      <div className="mt-6 p-5 rounded-3xl border border-gray-200 dark:border-white/10">
                        <div className="text-lg font-extrabold mb-3">
                          Pre Experience
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <TextField
                            label="YY"
                            value={emp.fld_preexpyrs}
                            onChange={(v) => setField("fld_preexpyrs", v)}
                            placeholder="Years"
                            inputBase={inputBase}
                            labelBase={labelBase}
                            type="text"
                          />

                          <TextField
                            label="MM"
                            value={emp.fld_preexpmonths}
                            onChange={(v) => setField("fld_preexpmonths", v)}
                            placeholder="Months"
                            inputBase={inputBase}
                            labelBase={labelBase}
                            type="text"
                          />
                        </div>

                        <div className="text-xs opacity-60 mt-2">
                          ASP accepts numbers only (YY/MM).
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {tab === "status" && (
                  <>
                    <div className="text-xl font-extrabold mb-4">Status</div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Gross / HRA */}
                      <TextField
                        label="Gross Salary"
                        required
                        value={emp.fld_grosssalary}
                        onChange={(v) => setField("fld_grosssalary", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <TextField
                        label="Special HRA"
                        required
                        value={emp.fld_SpecialHRA}
                        onChange={(v) => setField("fld_SpecialHRA", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      <TextField
                        label="Conveyance Allowance"
                        required
                        value={emp.fld_conveyanceallowance}
                        onChange={(v) => setField("fld_conveyanceallowance", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* PF No */}
                      <TextField
                        label="PF No"
                        required
                        value={emp.fld_pfno}
                        onChange={(v) => setField("fld_pfno", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* PF (ASP radio: Yes/No/Honorarium) */}
                      <RadioField
                        label="PF"
                        required
                        value={emp.fld_pf}
                        onChange={(v) => setField("fld_pf", v)}
                        options={[
                          { value: "Yes", label: "Yes" },
                          { value: "No", label: "No" },
                          { value: "Honorarium", label: "Honorarium" },
                        ]}
                        isDark={isDark}
                        labelBase={labelBase}
                      />

                      {/* TDS */}
                      <TextField
                        label="TDS"
                        required
                        value={emp.fld_tds}
                        onChange={(v) => setField("fld_tds", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* PF UAN */}
                      <TextField
                        label="PF UAN No"
                        required
                        value={emp.fld_pfuanno}
                        onChange={(v) => setField("fld_pfuanno", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Grade */}
                      <SelectField
                        label="Grade"
                        required
                        value={emp.fld_gradeid}
                        onChange={(v) => setField("fld_gradeid", v)}
                        options={(masters.grades || []).map((g) => ({
                          value: g.gradeId,
                          label: g.gradeName,
                        }))}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Branch */}
                      <SelectField
                        label="Branch"
                        required
                        value={emp.fld_branchid}
                        onChange={(v) => setField("fld_branchid", v)}
                        options={(masters.branches || []).map((b) => ({
                          value: b.fld_Branchid ?? b.branchId ?? "",
                          label: b.fld_BranchName ?? b.branchName ?? "Branch",
                        }))}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* ESI radio */}
                      <RadioField
                        label="E.S.I."
                        required
                        value={emp.fld_esi}
                        onChange={(v) => setField("fld_esi", v)}
                        options={[
                          { value: "Yes", label: "Yes" },
                          { value: "No", label: "No" },
                        ]}
                        isDark={isDark}
                        labelBase={labelBase}
                      />

                      {/* ESI Number */}
                      <TextField
                        label="ESI Number"
                        required
                        value={emp.fld_ESIno}
                        onChange={(v) => setField("fld_ESIno", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* PAN (ASP shows here) */}
                      <TextField
                        label="PAN Number"
                        value={emp.fld_PANno}
                        onChange={(v) => setField("fld_PANno", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* SSN */}
                      <TextField
                        label="SSN Number"
                        value={emp.fld_ssnno || ""}
                        onChange={(v) => setField("fld_ssnno", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Shift dropdown (Day/Contract) */}
                      <SelectField
                        label="Shift"
                        required
                        value={emp.fld_shiftname || ""}
                        onChange={(v) => setField("fld_shiftname", v)}
                        options={[
                          { value: "Day", label: "Day" },
                          { value: "Contract", label: "Contract" },
                        ]}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Salary Basis dropdown (Daily/Monthly) */}
                      <SelectField
                        label="Salary Basis"
                        required
                        value={emp.fld_salarybasis || ""}
                        onChange={(v) => setField("fld_salarybasis", v)}
                        options={[
                          { value: "Daily", label: "Daily" },
                          { value: "Monthly", label: "Monthly" },
                        ]}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* CLA Allowance */}
                      <TextField
                        label="CLA Allowance"
                        value={emp.fld_claallowance || ""}
                        onChange={(v) => setField("fld_claallowance", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Daily Bata */}
                      <TextField
                        label="Daily Bata"
                        value={emp.fld_dailybata}
                        onChange={(v) => setField("fld_dailybata", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Leave Opening Balance */}
                      <TextField
                        label="Leave Opening Balance"
                        required
                        value={emp.fld_leaveOB}
                        onChange={(v) => setField("fld_leaveOB", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Leave Per Month */}
                      <TextField
                        label="Leave Per Month"
                        required
                        value={emp.fld_leavepermonth}
                        onChange={(v) => setField("fld_leavepermonth", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Leave Closing Balance */}
                      <TextField
                        label="Leave Closing Balance"
                        value={emp.fld_leaveclosingbalance || ""}
                        onChange={(v) => setField("fld_leaveclosingbalance", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Leave Eligible */}
                      <TextField
                        label="Leave Eligible"
                        value={emp.fld_leaveeligible || ""}
                        onChange={(v) => setField("fld_leaveeligible", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Income Tax */}
                      <div>
                        <div className={labelBase}>Income Tax</div>
                        <div className="flex items-center gap-2">
                          <input
                            className={inputBase}
                            value={emp.fld_incometax || ""}
                            onChange={(e) =>
                              setField("fld_incometax", e.target.value)
                            }
                          />
                          <span className="text-sm opacity-70">%</span>
                        </div>
                      </div>

                      {/* Avail Leave (Yes/No radio) */}
                      <RadioField
                        label="Avail Leave"
                        value={emp.fld_availleave || ""}
                        onChange={(v) => setField("fld_availleave", v)}
                        options={[
                          { value: "Yes", label: "Yes" },
                          { value: "No", label: "No" },
                        ]}
                        isDark={isDark}
                        labelBase={labelBase}
                      />

                      {/* Salary Scheme */}
                      <SelectField
                        label="Salary Scheme"
                        required
                        value={emp.fld_schemeid}
                        onChange={(v) => setField("fld_schemeid", v)}
                        options={(masters.schemes || []).map((s) => ({
                          value: s.fld_schemeid ?? s.schemeId ?? "",
                          label: s.fld_schemename ?? s.schemeName ?? "Scheme",
                        }))}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Payment Mode (Cash/Bank radio) */}
                      <RadioField
                        label="Payment Mode"
                        required
                        value={emp.fld_paymode || ""}
                        onChange={(v) => setField("fld_paymode", v)}
                        options={[
                          { value: "Cash", label: "Cash" },
                          { value: "Bank", label: "Bank" },
                        ]}
                        isDark={isDark}
                        labelBase={labelBase}
                      />

                      {/* Bonus Bata (Yes/No radio) */}
                      <RadioField
                        label="Bonus Bata"
                        value={emp.fld_bonusbata || ""}
                        onChange={(v) => setField("fld_bonusbata", v)}
                        options={[
                          { value: "Yes", label: "Yes" },
                          { value: "No", label: "No" },
                        ]}
                        isDark={isDark}
                        labelBase={labelBase}
                      />

                      {/* Voter Id (ASP shows here) */}
                      <TextField
                        label="Voter Id"
                        value={emp.fld_voterid}
                        onChange={(v) => setField("fld_voterid", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Aadhar Card Number (ASP shows here) */}
                      <TextField
                        label="Aadhar Card Number"
                        value={emp.fld_Aadharcardnumber}
                        onChange={(v) => setField("fld_Aadharcardnumber", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* IFSC Code */}
                      <TextField
                        label="IFSC Code"
                        value={emp.fld_IFSCCode || ""}
                        onChange={(v) => setField("fld_IFSCCode", v)}
                        inputBase={inputBase}
                        labelBase={labelBase}
                      />

                      {/* Details textarea (ASP has Details) */}
                      <div className="md:col-span-2">
                        <div className={labelBase}>Details</div>
                        <textarea
                          className={`${inputBase} min-h-[110px]`}
                          value={emp.fld_details || ""}
                          onChange={(e) =>
                            setField("fld_details", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* Remarks (ASP shows title + details) */}
                    <div className="mt-6">
                      <div className="text-lg font-extrabold mb-2">Remarks</div>
                      <div
                        className={`p-4 rounded-2xl border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <div className={labelBase}>
                              Remarks Details Title
                            </div>
                            <input
                              className={inputBase}
                              value={remarkTitle}
                              onChange={(e) => setRemarkTitle(e.target.value)}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <div className={labelBase}>Remarks Details</div>
                            <textarea
                              className={`${inputBase} min-h-[110px]`}
                              value={remarkDetails}
                              onChange={(e) => setRemarkDetails(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeManagementForm;
